<template>
  <div>
    <div ref="dropdown" class="dropdown" v-if="type === 'select'">
      <div class="select">
        <span id="selected" class="selected">{{ selectLabel }}</span>
        <i class="material-icons">keyboard_arrow_down</i>
      </div>
      <input type="hidden" />
      <ul ref="dropdownMenu" class="dropdown-menu">
        <span>{{ resources.component.customInput.company }}</span>
        <li
          :id="companyName + 'Id'"
          @click="selectGroupItem({ name: companyName, _id: 'company' })"
        >
          {{ companyName }}
        </li>
        <br />
        <span v-if="selectList.length">
          {{ resources.component.customInput.groups }}
        </span>
        <li
          v-for="(group, index) in selectList"
          :id="group.name + 'Id'"
          :value="group._id"
          :key="index + group._id"
          @click="selectGroupItem(group)"
        >
          {{ group.name }}
        </li>
      </ul>
    </div>

    <b-form-group v-else>
      <b-form-input
        v-if="!hasMask"
        :class="{
          'has-label': hasLabel,
          'is-code': name === 'code',
          'has-code': name === 'code' && textValue && textValue !== '',
        }"
        :required="isRequired"
        :ref="refProp"
        v-model="textValue"
        :type="typeCheck"
        :disabled="!!disabled"
        :pattern="!valid[type](textValue) || emailEntered || emailExists"
        :title="returnTitle()"
        :placeholder="placeholder"
        :id="alias()"
        :state="customState && !emailEntered && !emailExists"
        :change="verify('change')"
        @blur.native="verify('blur')"
      >
      </b-form-input>
      <b-form-input
        v-else
        :class="{
          'has-label': name === 'date',
          'is-date': name === 'date',
          'has-date': name === 'date' && textValue && textValue !== '',
        }"
        :required="isRequired"
        :ref="refProp"
        v-model="textValue"
        v-mask="inputMask[name]()"
        :type="typeCheck"
        :disabled="!!disabled"
        :pattern="!valid[type](textValue) || emailEntered || emailExists"
        :title="returnTitle()"
        :placeholder="placeholder"
        :id="alias()"
        :state="customState && !emailEntered && !emailExists"
        :change="verify('change')"
        @blur.native="verify('blur')"
      >
      </b-form-input>
      <label
        :id="alias() + 'Label'"
        :class="'custom-input-label ' + (labelUp ? 'up-label' : '')"
        :for="alias()"
      >
        {{ labelEmailEntered() || labelEmailExists() || labelMessage() }}
      </label>
    </b-form-group>

    <div class="dropdown-blocked" v-show="show" @click="show = false"></div>

    <div class="password-strength-indicator" v-if="hasPasswordConfirm">
      <p class="indicator" v-if="passwordRules.length">
        <i
          class="material-icons text-secondary"
          aria-hidden="true"
          v-if="!has_length"
        >
          close
        </i>
        <i class="material-icons text-success" aria-hidden="true" v-else>
          check
        </i>
        <span :class="{ 'text-gray01': has_length }">
          {{
            passwordRulesLabels.length | parse({ LENGTH: passwordRules.length })
          }}
        </span>
      </p>
      <p class="indicator" v-if="passwordRules.upperCase">
        <i
          class="material-icons text-secondary"
          aria-hidden="true"
          v-if="!has_uppercase"
        >
          close
        </i>
        <i class="material-icons text-success" aria-hidden="true" v-else>
          check
        </i>
        <span :class="{ 'text-gray01': has_uppercase }">
          {{
            passwordRulesLabels.upperCase
              | parse({ UPPERCASE: passwordRules.upperCase })
          }}
        </span>
      </p>
      <p class="indicator" v-if="passwordRules.lowerCase">
        <i
          class="material-icons text-secondary"
          aria-hidden="true"
          v-if="!has_lowercase"
        >
          close
        </i>
        <i class="material-icons text-success" aria-hidden="true" v-else>
          check
        </i>
        <span :class="{ 'text-gray01': has_lowercase }">
          {{
            passwordRulesLabels.lowerCase
              | parse({ LOWERCASE: passwordRules.lowerCase })
          }}
        </span>
      </p>
      <p class="indicator" v-if="passwordRules.numbers">
        <i
          class="material-icons text-secondary"
          aria-hidden="true"
          v-if="!has_numbers"
        >
          close
        </i>
        <i class="material-icons text-success" aria-hidden="true" v-else>
          check
        </i>
        <span :class="{ 'text-gray01': has_numbers }">
          {{
            passwordRulesLabels.numbers
              | parse({ NUMBERS: passwordRules.numbers })
          }}
        </span>
      </p>
      <p class="indicator" v-if="passwordRules.special">
        <i
          class="material-icons text-secondary"
          aria-hidden="true"
          v-if="!has_special"
        >
          close
        </i>
        <i class="material-icons text-success" aria-hidden="true" v-else>
          check
        </i>
        <span :class="{ 'text-gray01': has_special }">
          {{
            passwordRulesLabels.special
              | parse({ SPECIAL: passwordRules.special })
          }}
        </span>
      </p>
    </div>

    <b-form-group v-if="hasPasswordConfirm" class="mb-4">
      <b-form-input
        :required="isRequired"
        type="password"
        autocomplete="off"
        v-model="passwordConfirm.value"
        :pattern="!passwordConfirm.state"
        :title="confirmTitle"
        :id="'passwordConfirm' + (index || '')"
        :state="passwordConfirm.state"
        :change="verifyConfirmPassword('change')"
        @blur.native="verifyConfirmPassword('blur')"
      >
      </b-form-input>
      <label
        class="custom-input-label"
        :for="'passwordConfirm' + (index || '')"
      >
        {{ passwordConfirm.labelMessage() }}
      </label>
    </b-form-group>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { isEmail } from '@/helpers/validator'
import { mask } from 'vue-the-mask'

export default {
  directives: { mask },

  props: {
    index: undefined,
    name: undefined,
    text: undefined,
    value: undefined,
    disabled: {
      type: Boolean,
      default: false,
    },
    isRequired: {
      type: Boolean,
      default: true,
    },
    title: undefined,
    label: undefined,
    labelWrong: undefined,
    confirmTitle: undefined,
    confirmLabel: undefined,
    confirmLabelWrong: undefined,
    hasPasswordConfirm: undefined,
    placeholder: undefined,
    emailExists: {
      type: Boolean,
      default: false,
    },
    emailExistsText: undefined,
    emailEntered: {
      type: Boolean,
      default: false,
    },
    emailEnteredText: undefined,
    labelUp: {
      type: Boolean,
      default: false,
    },
    hasMask: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'text',
    },
    hasLabel: {
      type: Boolean,
      default: true,
    },
    state: {
      type: Boolean,
      default: undefined,
    },
    refProp: {
      type: String,
      default: '',
    },
    passwordRules: {
      type: Object,
      default: () => ({
        length: 8,
        numbers: 1,
        lowerCase: 0,
        upperCase: 1,
        special: 1,
      }),
    },
    disablePasswordVerifications: {
      type: Boolean,
      default: false,
    },
  },

  watch: {
    state: {
      handler(value) {
        this.customState = value
      },
      immediate: true,
    },
    value(value) {
      this.textValue = value
    },
  },

  data() {
    return {
      customState: undefined,
      textValue: this.value,
      show: false,
      selectLabel: undefined,

      passwordConfirm: {
        value: undefined,
        state: undefined,
        labelMessage: () =>
          this.passwordConfirm.state !== false
            ? this.confirmLabel
            : this.confirmLabelWrong,
      },

      returnTitle: () => {
        if (this.emailExists) return this.emailExistsText
        if (this.emailEntered) return this.emailEnteredText
        return this.title
      },
      inputMask: {
        phoneNumber: () => ['(##) ####-####', '(##) #####-####'],
        date: () => ['##/##/####'],
      },

      labelEmailExists: () =>
        this.emailExists ? this.emailExistsText : undefined,
      labelEmailEntered: () =>
        this.emailEntered ? this.emailEnteredText : undefined,
      labelMessage: () =>
        this.customState !== false ? this.label : this.labelWrong,

      valid: {
        tel: value => (value ? value.length > 13 : undefined),
        date: value => (value ? value.length > 9 : undefined),
        text: value => (value ? value.length > 0 : undefined),
        email: value => (value ? isEmail(value || '') : undefined),
        password: value => {
          if (!value) return undefined

          if (this.disablePasswordVerifications) return true

          const {
            length = 8, // mínimo de caractéres
            numbers = 1, // mínimo de números
            lowerCase = 0, // mínimo de caractéres minúsculos
            upperCase = 1, // mínimo de caractéres maiúsculos
            special = 1, // mínimo de caractéres especiais
          } = this.passwordRules

          // validating numbers quantity
          const numbersMin = this.getMinimun(numbers)
          this.has_numbers =
            !numbersMin || this.countMatches(value, /\d/g) >= numbersMin

          // validating lowerCase quantity
          const lowerCaseMin = this.getMinimun(lowerCase)
          this.has_lowercase =
            !lowerCaseMin || this.countMatches(value, /[a-z]/g) >= lowerCaseMin

          // validating upperCase quantity
          const upperCaseMin = this.getMinimun(upperCase)
          this.has_uppercase =
            !upperCaseMin || this.countMatches(value, /[A-Z]/g) >= upperCaseMin

          // validating special quantity
          const specialMin = this.getMinimun(special)
          this.has_special =
            !specialMin ||
            this.countMatches(value, /[!@#$%^&*)(+=._-]/g) >= specialMin

          // validating length
          const lengthMin = this.getMinimun(length)
          this.has_length = !lengthMin || value.length >= lengthMin

          return (
            this.has_numbers &&
            this.has_lowercase &&
            this.has_uppercase &&
            this.has_special &&
            this.has_length
          )
        },
        passwordConfirm: (p1, p2, state) =>
          p2 ? !!(p1 === p2 && state) : undefined,
      },

      alias: () => (this.name || this.type) + (this.index || ''),

      has_length: false,
      has_numbers: false,
      has_lowercase: false,
      has_uppercase: false,
      has_special: false,
    }
  },

  computed: {
    ...mapGetters({
      resources: 'resources',
    }),

    typeCheck() {
      return this.type === 'date' ? 'tel' : this.type
    },

    passwordRulesLabels() {
      return this.resources.component.customInput.passwordRules
    },
  },

  methods: {
    getMinimun(n) {
      if (!n) return 0
      return n
    },

    countMatches(value, regex) {
      const matches = value.match(regex) || []
      return matches.length
    },

    verify(event) {
      this.$emit('update', { value: this.textValue, event: event })
      this.$emit('input', this.textValue)

      if (event === 'blur') {
        if (this.type === 'password') this.verifyConfirmPassword('blur')
      }

      let state =
        !this.emailExists &&
        !this.emailEntered &&
        this.valid[this.type](this.textValue)

      if (this.customState === state) return

      if (this.state !== false) {
        if (event === 'blur') {
          this.customState = state
        } else {
          this.customState = state ? true : false
        }
      }
    },

    verifyConfirmPassword(event) {
      let validation = this.valid.passwordConfirm(
        this.textValue,
        this.passwordConfirm.value,
        this.customState
      )

      if (this.passwordConfirm.state === validation) return

      if (event === 'blur') this.passwordConfirm.state = validation
      else
        this.passwordConfirm.state = validation
          ? true
          : !!this.passwordConfirm.value
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/styles/custom/colors';

.password-strength-indicator {
  width: 400px;
  background: white;
  border-radius: 3px;
  padding-bottom: 10px;
  padding-top: 2px;

  .indicator {
    font-size: 0.8em;
    margin: 0;
  }
}
</style>
